import React, { useRef, useState } from "react";

import { ScreenReaderOnly } from "@edclub/accessibility";

import "./IntroWithApp.scss";
import { useOnMountUnsafe } from "@edclub/hooks";

export interface IntroProps {
  className?: string;
  introText: string;
  background?: React.ReactNode;
  backgroundColor?: string;
  children?: React.ReactElement;
  onWhite?: boolean;
  textColor?: string;
  audio?: any;
  audioText?: string | null;
  skip?: boolean;
  variant?: "default" | "justAbove" | "slideUp";
  isBlind?: boolean;
}

const IntroWithApp = ({
  className,
  introText,
  background,
  backgroundColor,
  children,
  onWhite = false,
  // Unset by default so the stylesheet's colour (and its dark-mode swap)
  // applies; an explicit prop still wins.
  textColor,
  audio = null,
  audioText = null,
  skip = false,
  variant = "default",
  isBlind,
}: IntroProps) => {
  const [introDone, setIntroDone] = useState(false);
  const [audioDone, setAudioDone] = useState(false);
  const [audioAnimation, setAudioAnimation] = useState(false);
  const introTextRef = useRef<HTMLParagraphElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const backgroundRef = useRef<HTMLDivElement>(null);
  const audioTagRef = useRef<any>(null);
  const shouldPlayRef = useRef(true);

  const handleCloseIntro = () => {
    setIntroDone(true);
    setAudioDone(true);
    setAudioAnimation(true);
    shouldPlayRef.current = false;
  };

  const handleVanishText = () => {
    if (audioTagRef.current && variant === "slideUp") {
      return;
    }

    if (audioTagRef.current) {
      audioTagRef.current.stop?.();
      window.approuter.stopAudio(audioTagRef.current);
    }

    handleCloseIntro();
  };

  useOnMountUnsafe(() => {
    if (skip) {
      if (contentRef.current) {
        contentRef.current.style.opacity = "1";
      }

      if (backgroundRef.current) {
        backgroundRef.current.style.opacity = "1";
      }

      setIntroDone(true);
      setAudioDone(true);

      return;
    }

    if (isBlind) {
      const abortController = new AbortController();

      window.addEventListener(
        "keydown",
        (e) => {
          if (e.key === "Escape" || e.key === "Tab") {
            handleCloseIntro();
            abortController.abort();
          }
        },
        { signal: abortController.signal },
      );

      return;
    }

    // TODO play audio or speak
    const playAudio = async () => {
      let tag;

      try {
        if (audio) {
          tag = await window.approuter.playAudio(audio);
        } else {
          tag = await approuter.speak(audioText || introText, {
            autoplay: false,
          });

          if (shouldPlayRef.current && tag) {
            await approuter.playAudio(tag);
          }
        }
      } catch {
        handleCloseIntro();
        return;
      }

      if (!tag || tag.id === -1 /* noop tag from createTag */) {
        handleCloseIntro();
        return;
      }

      audioTagRef.current = tag;
      await window.approuter.waitAudio(tag);

      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;

      if (prefersReducedMotion) {
        handleCloseIntro();
      } else {
        setAudioAnimation(true);
      }
    };

    playAudio();
  });

  return (
    <div
      className={className}
      style={introDone ? {} : { height: "100vh", overflow: "hidden" }}
    >
      {!audioDone && (
        <div
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
          className={`intro-text ${onWhite ? "white-bg" : ""})`}
          style={{
            color: textColor,
            cursor: variant === "slideUp" ? "unset" : "pointer",
          }}
          onClick={handleVanishText}
        >
          <p
            ref={introTextRef}
            className={`p_${variant} animate-${variant} ${
              audioAnimation && `fadeOut-${variant}`
            }`}
            onAnimationEnd={(e) => {
              if (e.animationName.startsWith("fadeOut")) {
                setAudioDone(true);
              }
            }}
          >
            {introText}
            {isBlind && (
              <ScreenReaderOnly as="span">
                {t("Press Escape or Tab key to close this message.")}
              </ScreenReaderOnly>
            )}
            {variant === "default" && <span className="skip-label">skip</span>}
          </p>
        </div>
      )}

      {(background || backgroundColor) && (
        <div
          className="intro-background background-animation"
          ref={backgroundRef}
          style={{ opacity: 0, background: backgroundColor }}
        >
          {background}
        </div>
      )}
      <div
        ref={contentRef}
        id="content"
        className={`app-wrapper ${
          !skip
            ? `animate-content-partial ${
                audioAnimation && introDone ? "animate-content-full" : ""
              }`
            : ""
        }`}
        style={{ opacity: 0 }}
        onAnimationEnd={(e: any) => {
          if (e.target.id === "content") {
            if (contentRef.current) {
              contentRef.current.style.transform = "none";
            }

            setIntroDone(true);
          }
        }}
      >
        {children &&
          React.cloneElement(children, { introDone, audioDone } as any)}
      </div>
    </div>
  );
};

export { IntroWithApp };
